import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    honeypot: '',
    captchaAnswer: ''
  });

  const [captcha, setCaptcha] = useState({ num1: 0, num2: 0 });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const formStartTime = new Date().getTime() / 1000;
  const baseUrl = process.env.REACT_APP_API_URL;
  const canonicalUrl = `${baseUrl}${window.location.pathname}${window.location.search}`;

  // Generate random math problem on load
  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ num1, num2 });
    setFormData(prev => ({ ...prev, captchaAnswer: '' }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check math CAPTCHA
    const correctAnswer = captcha.num1 + captcha.num2;
    if (parseInt(formData.captchaAnswer) !== correctAnswer) {
      setStatus('✗ CAPTCHA answer is incorrect');
      generateCaptcha();
      return;
    }

    setLoading(true);
    setStatus('Sending...');

    try {
      const submissionData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        honeypot: formData.honeypot,
        timestamp: formStartTime
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });

      const result = await response.json();

      if (response.ok) {
        setStatus('✓ Message sent successfully!');
        setFormData({ name: '', email: '', phone: '', message: '', honeypot: '', captchaAnswer: '' });
        generateCaptcha();
      } else {
        setStatus(`✗ ${result.error || 'Failed to send message'}`);
      }
    } catch (error) {
      setStatus('✗ Connection error. Please try again.');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact CSOAUTOMATION™ - Get in Touch</title>
        <link rel="canonical" href={canonicalUrl} />
        <meta name="description" content="Contact the CSOAUTOMATION™ team for inquiries, support, or general questions. We're here to help!" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h2 className="text-3xl font-bold mb-8">Contact Us</h2>

        {/* Contact Form */}
        <div className="bg-white bg-opacity-10 p-8 rounded-lg mb-12">
          <h3 className="text-2xl font-bold mb-6">Send us a Message</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block font-semibold mb-2">Name:</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">Email:</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">Phone (Optional):</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(123) 456-7890"
                className="w-full px-4 py-2 border border-gray-300 rounded text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block font-semibold mb-2">Message:</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="5"
                className="w-full px-4 py-2 border border-gray-300 rounded text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <input
              type="text"
              name="honeypot"
              value={formData.honeypot}
              onChange={handleChange}
              style={{ display: 'none' }}
              tabIndex="-1"
              autoComplete="off"
            />

            {/* Math CAPTCHA */}
            <div className="p-4 rounded border" style={{ backgroundColor: 'rgba(0, 0, 10, 0.5)' }}>
              <label className="block font-semibold mb-2 text-white">
                Security Check: What is {captcha.num1} + {captcha.num2}?
              </label>
              <input
                type="number"
                name="captchaAnswer"
                value={formData.captchaAnswer}
                onChange={handleChange}
                required
                placeholder="Enter the answer"
                className="w-full px-4 py-2 border border-gray-300 rounded text-white focus:outline-none focus:border-blue-500"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Message'}
            </button>

            {status && (
              <p className={`text-center font-semibold ${status.includes('✓') ? 'text-green-400' : 'text-red-400'}`}>
                {status}
              </p>
            )}
          </form>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Location Section */}
          <div className="bg-white bg-opacity-10 p-6 rounded-lg">
            <h3 className="text-xl font-bold mb-4">Location</h3>
            <p className="text-sm">Dallas, TX</p>
          </div>

          {/* Phone Section */}
          <div className="bg-white bg-opacity-10 p-6 rounded-lg">
            <h3 className="text-xl font-bold mb-4">Phone</h3>
            <p className="text-sm">
              Message us and we will get right back to you!
            </p>
          </div>
        </div>
      </div>
    </>
  );
}